import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardSharedTable from '@components/pages/Dashboard/Shared/DashboardSharedTable'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import type { ProviderUtilizationOverviewRow } from '@services/api/dashboard/provider-utilization.api'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { PROVIDER_UTILIZATION_COLUMNS } from '../../Overview/dashboardOverview.mockData'

type ProviderUtilizationCardProps = {
  rows: ProviderUtilizationOverviewRow[]
  loading: boolean
  /** Provider utilization response has arrived (`data != null`). */
  hasPayload: boolean
  titleHref?: string
  /** Defaults to 8. Pass `null` to show every row. */
  maxRows?: number | null
  maxHeight?: string
  disableBodyScroll?: boolean
  tableClassName?: string
}

export function ProviderUtilizationCard({
  rows: providerUtilizationRows,
  loading: providerUtilizationLoading,
  hasPayload,
  titleHref = DASHBOARD_DETAIL_HREFS.providerUtilization,
  maxRows = 8,
  maxHeight = '320px',
  disableBodyScroll = false,
  tableClassName,
}: ProviderUtilizationCardProps): React.ReactElement {
  // Idle time is capacity (a provider with no bookings is fully idle), so it
  // does not count as activity; utilization and booked time do.
  const hasData = providerUtilizationRows.some((row) =>
    hasNonZeroDashboardValues(row.utilization, row.bookedTime),
  )
  const isEmpty = shouldShowDashboardSectionEmpty(
    providerUtilizationLoading,
    hasData,
  )
  const pending = isDashboardSectionPending(
    providerUtilizationLoading,
    hasPayload,
  )

  const rows =
    maxRows == null
      ? providerUtilizationRows
      : providerUtilizationRows.slice(0, maxRows)
  const table = (
    <DashboardSharedTable
      loading={pending ? false : providerUtilizationLoading}
      showSearchFilter={false}
      columns={PROVIDER_UTILIZATION_COLUMNS}
      data={rows}
      showPagination={false}
      maxHeight={maxHeight}
      disableBodyScroll={disableBodyScroll}
    />
  )

  return (
    <DashboardSectionCard
      title="Provider utilization"
      titleHref={titleHref}
      loading={pending}
      skeleton={{ block: 'tall' }}
      isEmpty={isEmpty}
    >
      {tableClassName ? <div className={tableClassName}>{table}</div> : table}
    </DashboardSectionCard>
  )
}
